import { PlusOutlined } from "@ant-design/icons";
import { Button, Drawer, Form, message } from "antd";
import { useEffect, useState } from "react";

import TableCustom from "../../../components/ui/TableCustom";
import { Colors } from "../../../constants/Colors";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { prepareSingleFile } from "../utils/filePrepare";
import { EditOptions, FeedbackType } from "./content/dailyCheckInTypes";
import { createFeedbackColumns, createFeedbackOptionsColumns } from "./content/feedbackColumns";
import { fetchFeedbackList, updateFeedback } from "./services/dailyCheckIn.services";

const FeedbackTabContent = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [displayData, setDisplayData] = useState<FeedbackType[] | any>([]);
  const [editData, setEditData] = useState<FeedbackType | undefined>(undefined);
  const [editOptionsData, setEditOptionsData] = useState<EditOptions[] | undefined>(undefined);
  const [newRowCount, setNewRowCount] = useState(0);
  const [optionsMissing, setOptionsMissing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const handleEditClick = (record: FeedbackType) => {
    const optionsData = record.feedbackOptions.map((item) => {
      return {
        feedbackId: item.feedbackId,
        audioFile: [
          {
            uid: "0",
            name: item.audioFile,
            status: "done",
            url: item.audioUrl,
          }],
        badgeImageFile: [
          {
            uid: "0",
            name: item.badgeImageFile,
            status: "done",
            url: item.badgeImageUrl,
            thumbUrl: item.badgeImageUrl,
          }],
      }
    });

    setShowDrawer(true);
    setEditData(record);
    setEditOptionsData(optionsData);
  };

  const handleClose = () => {
    form.resetFields();
    setEditData(undefined);
    setEditOptionsData(undefined);
    setShowDrawer(false);
  }

  const handleSubmit = async () => {
    if (optionsMissing) return;
    setIsLoading(true);

    const payload = await Promise.all(
      (editOptionsData ?? []).map(async (item) => {
        const audioFile = await prepareSingleFile(item.audioFile);
        const badgeImageFile = await prepareSingleFile(item.badgeImageFile);

        return {
          ...(!item.feedbackId.toString().startsWith("new-") && { feedbackId: item.feedbackId }),
          characterName: editData?.characterName,
          audioFile,
          badgeImageFile,
        };
      })
    );

    const data = await updateFeedback(payload);

    if (data?.status === "SUCCESS") {
      getFeedbackList();
      handleClose();
      message.success(renderAntDMessageConfig("Update successful"));
    } else {
      message.error(renderAntDMessageConfig("Update failed"));
    }

    setIsLoading(false);
  };

  const handleOptionAddClick = async () => {
    if (!editOptionsData) return;

    const newOption = {
      feedbackId: `new-${newRowCount}`,
      audioFile: [],
      badgeImageFile: [],
    };

    setEditOptionsData([...editOptionsData, newOption]);
    setNewRowCount(newRowCount + 1);
  }

  const handleOptionDeleteClick = async (record: EditOptions) => {
    const date = editOptionsData?.filter(item => item.feedbackId !== record.feedbackId);
    setEditOptionsData(date);
  }

  const getFeedbackList = async () => {
    const data = await fetchFeedbackList();

    if (data) setDisplayData(data.data);
  }

  useEffect(() => {
    if (editOptionsData && editOptionsData.length > 0) {
      const missingItems = editOptionsData.filter(
        (item) => !item.audioFile?.length || !item.badgeImageFile?.length
      );

      setOptionsMissing(missingItems.length > 0);
    } else
      setOptionsMissing(false);
  }, [editOptionsData]);

  useEffect(() => {
    getFeedbackList();
  }, []);

  return (
    <>
      <div className="table-container">
        <TableCustom
          rowKey={(record: FeedbackType) => record.characterName}
          columns={createFeedbackColumns(handleEditClick)}
          dataSource={displayData}
          pagination={false}
        />
      </div>
      <div id="feedbackTabContentWrapper">
        <Drawer
          title={`Edit ${editData?.characterName} feedback`}
          open={showDrawer}
          onClose={() => handleClose()}
          width={500}
        >
          <Form
            name="feedback-form"
            form={form}
            layout="vertical"
          >
            <Form.Item
              className="form-item"
              label="Character"
              name="characterName"
            >
              <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                {editData?.characterName}
              </p>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Feedback"
              name="feedbackOptions"
            >
              <TableCustom
                rowKey={(record: EditOptions) => record.feedbackId}
                columns={createFeedbackOptionsColumns(setEditOptionsData, handleOptionDeleteClick)}
                dataSource={editOptionsData}
                pagination={false}
              />
              {optionsMissing && (
                <p style={{ marginTop: "5px", color: "red" }}>This field is required!</p>
              )}
              <Button
                type="dashed"
                onClick={() => handleOptionAddClick()}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: "20px", marginBottom: "30px" }}
              >
                Add Row
              </Button>
            </Form.Item>
          </Form>
          <DrawerFooter
            isLoading={isLoading}
            handleSubmit={() => {
              handleSubmit();
            }}
            handleCancel={() => {
              form.resetFields();
              setEditData(undefined);
              setEditOptionsData(undefined);
              setShowDrawer(false);
            }}
          />
        </Drawer>
      </div>
    </>
  );
};

export default FeedbackTabContent;
